import { AES, enc } from "crypto-js";
import { Buses, Capacity_Costs } from "generated/graphql";
import { assertMaybeToValue } from "graphql/helpers/queryHelpers";
import { convertToRegionType, RegionType } from "./regionType";
import { convertToVoltage, isKnownVoltageType, Voltage } from "./voltageTypes";

const SECRET_KEY_FOR_BUS_ID = "NIRA_BUS_ID_SECRET_KEY";

export type BusId = string;
export type NiraBusId = string;
export type EncryptedBusId = string;

export const UnauthorizedCapacityCosts = "unauthorizedCapacityCosts";
export type UnauthorizedCapacityCostsType = typeof UnauthorizedCapacityCosts;

export type ScopedBus = {
    readonly bus: Bus;
    readonly scopedCapacityEnergyCost: CapacityEnergyCost;
    readonly zeroMwConstraintCount?: number;
};

export const isScopedBus = (
    bus: ScopedBus | UnauthorizedBus
): bus is ScopedBus => {
    return bus.scopedCapacityEnergyCost !== UnauthorizedCapacityCosts;
};

export type UnauthorizedBus = {
    readonly bus: Bus;
    readonly scopedCapacityEnergyCost: UnauthorizedCapacityCostsType;
};

export const isUnauthorizedBus = (
    bus: ScopedBus | UnauthorizedBus
): bus is UnauthorizedBus => {
    return bus.scopedCapacityEnergyCost === UnauthorizedCapacityCosts;
};

export const HIGH_CONFIDENCE_LOCATION_CODES = [1, 2];
export const LOW_CONFIDENCE_LOCATION_CODES = [3, 4];
export const ALL_LOCATION_CODES = [
    ...HIGH_CONFIDENCE_LOCATION_CODES,
    ...LOW_CONFIDENCE_LOCATION_CODES
];
export const isLowConfidenceLocationBus = (bus: Bus): boolean => {
    return LOW_CONFIDENCE_LOCATION_CODES.includes(bus.locationCode);
};

// True when the pipeline flagged this bus as belonging to an entity that has left the
// product's region -- e.g. a WECC owner that joined SPP in the 2026-04-01 RTO Expansion
// (RTOE). Backed by the dedicated cross-region `out_of_region` column (nira-hasura), so
// it is independent of location_code / geocoding confidence (a bus can be BOTH
// out-of-region and low-confidence). The WECC bus detail pane and the bus details
// page show an RTOE note for these; those callouts are the only surfaces (they are
// intentionally NOT grayed on the map).
export const isOutOfRegionBus = (bus: Bus): boolean => {
    return bus.outOfRegion === true;
};

export type Bus = {
    readonly id: BusId;
    readonly niraBusId: NiraBusId;
    readonly voltage: Voltage;
    readonly busDisplayName: string;
    readonly longitude: number;
    readonly latitude: number;
    readonly region: RegionType;
    readonly locationCode: number;
    readonly ownerName: string;
    // True if the bus is out-of-region (see isOutOfRegionBus). Optional: only bus
    // queries that select out_of_region populate it; absent means in-region.
    readonly outOfRegion?: boolean;
    // The utility that operates the bus, as the WECC study resolves it. One per
    // study direction, because each direction is derived in its own model
    // (nira-data-pipeline's add_wecc_bus_utilities). Absent for a query that
    // does not select the columns, for every non-WECC region, and for a bus the
    // study drops (outside the WECC control areas, or with no utility
    // resolvable nearby).
    readonly utilityDischarging?: string;
    readonly utilityStorage?: string;
    // Planned MW for this bus' Planned Interconnection Location (PIL), if it is
    // one. Undefined for buses that are not on the region's PIL list.
    readonly plannedMw?: number;
};

export type CapacityEnergyCost = {
    readonly capacitySize: number;
    readonly energySize: number;
    readonly chargingSize: number;
    readonly allocatedCosts: number;
    readonly totalCosts: number;
};

type HasuraConstraintStacksAggregate = {
    readonly aggregate?: {
        readonly count: number;
    } | null;
};

type HasuraBusWithCapacityCosts = Readonly<
    Pick<
        Buses,
        | "id"
        | "nira_bus_id"
        | "voltage"
        | "latitude"
        | "longitude"
        | "bus_display_name"
        | "region"
        | "location_code"
        | "owner_name"
        | "out_of_region"
    > & {
        capacity_costs: Array<HasuraCapacityCost>;
        constraint_stacks_aggregate?: HasuraConstraintStacksAggregate;
    }
>;

type HasuraBus = Readonly<
    Pick<
        Buses,
        | "id"
        | "nira_bus_id"
        | "voltage"
        | "latitude"
        | "longitude"
        | "bus_display_name"
        | "region"
        | "location_code"
        | "owner_name"
        | "out_of_region"
    > &
        // Optional rather than picked: only the bus-page query selects the
        // utility columns, and the map, search and model-line queries that
        // share this conversion do not.
        Partial<Pick<Buses, "utility_discharging" | "utility_storage">>
>;

type HasuraCapacityCost = Readonly<
    { __typename?: "capacity_costs" } & Pick<
        Capacity_Costs,
        | "capacity_size"
        | "energy_size"
        | "charging_size"
        | "upgrade_costs"
        | "total_upgrade_costs"
    >
>;

export const getCapacityEnergyCost = (
    hasuraCapacityCost: HasuraCapacityCost
): CapacityEnergyCost => {
    return {
        capacitySize: hasuraCapacityCost.capacity_size,
        energySize: hasuraCapacityCost.energy_size,
        chargingSize: hasuraCapacityCost.charging_size,
        allocatedCosts: hasuraCapacityCost.upgrade_costs,
        totalCosts: hasuraCapacityCost.total_upgrade_costs
    };
};

export const hasuraBusToNiraBus = (hasuraBus: HasuraBus): Bus => {
    // have to convert values to non-nullable types since buses is now a view in Hasura
    // so all of it's fields are nullable, even though we know they are not null
    return {
        id: assertMaybeToValue(hasuraBus.id),
        niraBusId: assertMaybeToValue(hasuraBus.nira_bus_id),
        voltage: convertToVoltage(assertMaybeToValue(hasuraBus.voltage)),
        busDisplayName: assertMaybeToValue(hasuraBus.bus_display_name),
        longitude: assertMaybeToValue(hasuraBus.longitude),
        latitude: assertMaybeToValue(hasuraBus.latitude),
        region: convertToRegionType(assertMaybeToValue(hasuraBus.region)),
        locationCode: assertMaybeToValue(hasuraBus.location_code),
        ownerName: assertMaybeToValue(hasuraBus.owner_name),
        outOfRegion: hasuraBus.out_of_region ?? false,
        utilityDischarging: hasuraBus.utility_discharging ?? undefined,
        utilityStorage: hasuraBus.utility_storage ?? undefined
        // plannedMw is merged in separately from the PIL query (see useBusData),
        // so it is intentionally not set here.
    };
};

export const convertHasuraBusesToNiraBusesAndSplitBasedOnAuth = (
    hasuraBuses: ReadonlyArray<HasuraBusWithCapacityCosts>
): readonly [ReadonlyArray<ScopedBus>, ReadonlyArray<UnauthorizedBus>] => {
    let authorizedBuses: ReadonlyArray<ScopedBus> = [];
    let unauthorizedBuses: ReadonlyArray<UnauthorizedBus> = [];

    const filteredHasuraBusesKnownVoltages = hasuraBuses.filter((hasuraBus) => {
        return isKnownVoltageType(assertMaybeToValue(hasuraBus.voltage));
    });

    filteredHasuraBusesKnownVoltages.forEach((hasuraBus) => {
        const convertedBusData = hasuraBusToNiraBus(hasuraBus);
        const isAuthorized = hasuraBus.capacity_costs.length > 0;

        if (!isAuthorized) {
            unauthorizedBuses = [
                ...unauthorizedBuses,
                {
                    scopedCapacityEnergyCost: UnauthorizedCapacityCosts,
                    bus: convertedBusData
                }
            ];
        } else {
            const zeroMwConstraintCount =
                hasuraBus.constraint_stacks_aggregate?.aggregate?.count;
            authorizedBuses = [
                ...authorizedBuses,
                {
                    scopedCapacityEnergyCost: getCapacityEnergyCost(
                        hasuraBus.capacity_costs[0]
                    ),
                    bus: convertedBusData,
                    zeroMwConstraintCount
                }
            ];
        }
    });

    return [authorizedBuses, unauthorizedBuses] as const;
};

export const encryptBusId = (busId: BusId): string => {
    return AES.encrypt(busId, SECRET_KEY_FOR_BUS_ID).toString();
};

export const decryptBusId = (busId: EncryptedBusId): string => {
    return AES.decrypt(busId, SECRET_KEY_FOR_BUS_ID).toString(enc.Utf8);
};

export const niraBusIdToBusId = (niraBusId: NiraBusId): BusId => {
    return niraBusId.split("-")[1];
};

export const niraBusIdToRegion = (niraBusId: NiraBusId): string => {
    return niraBusId.split("-")[0];
};
